import { createSlice } from "@reduxjs/toolkit";
import { immediateToast } from "izitoast-react";
import { StoreDispatch } from "../store";
import {
  internalUpdateWorkflowTemplate,
  updateWorkflow,
  UpdateWorkflowInput,
  Workflow
} from "src/network/graphql/workflowService";
import { setConflict, setDetail } from "src/page/workflowDetail/workflowDetailSlice";
import { upsertWorkflowInList } from "src/page/workflows/workflowsSlice";
import { isConflictError } from "src/utils/errors";

interface WorkflowEditState {
  saveLoading: boolean;
}

const initialState: WorkflowEditState = {
  saveLoading: false
};

const CONFLICT_MESSAGE =
  "Workflow was updated by someone else. Refresh to see the latest and try again.";

const workflowEditSlice = createSlice({
  name: "workflowEdit",
  initialState,
  reducers: {
    saveStart: (state) => {
      state.saveLoading = true;
    },
    saveFinish: (state) => {
      state.saveLoading = false;
    }
  }
});

export const { saveStart, saveFinish } = workflowEditSlice.actions;

export const updateWorkflowAction = (
  input: UpdateWorkflowInput,
  cb?: (_workflow: Workflow) => void,
  admin?: boolean
) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(saveStart());
    try {
      const response = admin
        ? await internalUpdateWorkflowTemplate(input)
        : await updateWorkflow(input);
      const workflow: Workflow | undefined = admin
        ? response?.data?.internal_update_workflow_template
        : response?.data?.update_workflow;

      dispatch(saveFinish());
      if (workflow) {
        dispatch(setDetail(workflow));
        dispatch(upsertWorkflowInList(workflow));
        immediateToast("success", {
          message: admin ? "Workflow template updated" : "Workflow updated",
          timeout: 3000,
          position: "topCenter"
        });
        cb?.(workflow);
      }
      return workflow ?? null;
    } catch (error) {
      dispatch(saveFinish());
      if (isConflictError(error)) {
        dispatch(setConflict(true));
        immediateToast("error", {
          message: CONFLICT_MESSAGE,
          timeout: 4000,
          position: "topCenter"
        });
      }
      return null;
    }
  };
};

export default workflowEditSlice.reducer;
